import { LOCATION_KEY, readLocation, browseArea, usePostcodeArea, lookupLocationPostcode, nearbyTownParam, locationLabel, type HubLocation } from "../../../../lib/location/selection";
import { LocationPicker } from "@/components/LocationPicker";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export interface TownEntry { name: string; lat: number; lon: number; borough?: string; }

export interface TownGroup { borough: string; towns: TownEntry[]; }

export const AYRSHIRE_TOWN_GROUPS: TownGroup[] = [
  {
    borough: "North Ayrshire",
    towns: [
      { name: "Irvine",        lat: 55.6110, lon: -4.6603 },
      { name: "Kilwinning",    lat: 55.6543, lon: -4.7014 },
      { name: "Ardrossan",     lat: 55.6453, lon: -4.8152 },
      { name: "Saltcoats",     lat: 55.6355, lon: -4.7881 },
      { name: "Stevenston",    lat: 55.6347, lon: -4.7530 },
      { name: "Largs",         lat: 55.7954, lon: -4.8697 },
      { name: "West Kilbride", lat: 55.6934, lon: -4.8622 },
      { name: "Kilbirnie",     lat: 55.7576, lon: -4.6842 },
      { name: "Beith",         lat: 55.7511, lon: -4.6347 },
      { name: "Dalry",         lat: 55.7098, lon: -4.7183 },
      { name: "Fairlie",       lat: 55.7592, lon: -4.8641 },
      { name: "Skelmorlie",    lat: 55.8398, lon: -4.8786 },
    ],
  },
  {
    borough: "East Ayrshire",
    towns: [
      { name: "Kilmarnock",    lat: 55.6115, lon: -4.4958 },
      { name: "Stewarton",     lat: 55.6842, lon: -4.5167 },
      { name: "Galston",       lat: 55.5982, lon: -4.3918 },
      { name: "Newmilns",      lat: 55.6032, lon: -4.3344 },
      { name: "Darvel",        lat: 55.5988, lon: -4.2856 },
      { name: "Mauchline",     lat: 55.5131, lon: -4.3748 },
      { name: "Cumnock",       lat: 55.4522, lon: -4.2688 },
      { name: "Auchinleck",    lat: 55.4676, lon: -4.2917 },
      { name: "Patna",         lat: 55.3528, lon: -4.3572 },
      { name: "Muirkirk",      lat: 55.5058, lon: -3.9994 },
    ],
  },
  {
    borough: "South Ayrshire",
    towns: [
      { name: "Ayr",           lat: 55.4659, lon: -4.6292 },
      { name: "Prestwick",     lat: 55.4959, lon: -4.6124 },
      { name: "Troon",         lat: 55.5397, lon: -4.6569 },
      { name: "Monkton",       lat: 55.5088, lon: -4.5797 },
      { name: "Symington",     lat: 55.5082, lon: -4.5983 },
      { name: "Maybole",       lat: 55.3542, lon: -4.6847 },
      { name: "Girvan",        lat: 55.2388, lon: -4.8586 },
      { name: "Ballantrae",    lat: 55.0972, lon: -5.0083 },
    ],
  },
];

const FALLBACK_TOWNS: TownEntry[] = AYRSHIRE_TOWN_GROUPS.flatMap(g => g.towns);

let _runtimeTowns: TownEntry[] = FALLBACK_TOWNS;

export function getAyrshireTowns(): string[] {
  return ["All Towns", ..._runtimeTowns.map(t => t.name)];
}

export let AYRSHIRE_TOWNS: string[] = getAyrshireTowns();

/** Build borough groups from a flat towns array that includes `borough` field. */
export function buildTownGroups(entries: TownEntry[]): TownGroup[] {
  if (entries.some(t => t.borough)) {
    const map = new Map<string, TownEntry[]>();
    for (const t of entries) {
      const b = t.borough ?? "Other";
      if (!map.has(b)) map.set(b, []);
      map.get(b)!.push(t);
    }
    return Array.from(map.entries()).map(([borough, towns]) => ({ borough, towns }));
  }
  // Legacy data without borough — fall back to hardcoded groups filtered by active names
  const nameSet = new Set(entries.map(t => t.name));
  return AYRSHIRE_TOWN_GROUPS
    .map(g => ({ ...g, towns: g.towns.filter(t => nameSet.has(t.name)) }))
    .filter(g => g.towns.length > 0);
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function nearestTown(lat: number, lon: number, towns: TownEntry[]): string {
  let best = "All Towns";
  let bestDist = Infinity;
  for (const t of towns) {
    const d = haversineKm(lat, lon, t.lat, t.lon);
    if (d < bestDist) { bestDist = d; best = t.name; }
  }
  return bestDist < 30 ? best : "All Towns";
}

const NEARBY_RADIUS_KM = 12;

function getNearbyTownNames(name: string, entries: TownEntry[]): string[] {
  const origin = entries.find(t => t.name === name);
  if (!origin) return [name];
  return entries
    .filter(t => haversineKm(origin.lat, origin.lon, t.lat, t.lon) <= NEARBY_RADIUS_KM)
    .map(t => t.name);
}

const STORAGE_KEY = "ayrshire_selected_town";

interface TownContextValue {
  selectedTown: string;
  deliveryPostcode: string | null;
  locationLabel: string;
  location: HubLocation;
  openLocationPicker: (onSelected?: (town: string) => void) => void;
  setSelectedTown: (town: string) => void;
  /** Currently scoped Ayrshire borough or `null` for All Ayrshire. */
  selectedRegion: string | null;
  setSelectedRegion: (region: string | null) => void;
  /** Town names visible under the active region (or all towns when null). */
  townsForSelectedRegion: string[];
  detecting: boolean;
  detectLocation: () => void;
  locationError: string | null;
  townParam: string | undefined;
  towns: string[];
  townGroups: TownGroup[];
}

const TownContext = createContext<TownContextValue>({
  selectedTown: "All Towns",
  deliveryPostcode: null, locationLabel: "Set your postcode", location: readLocation(null), openLocationPicker: () => {},
  setSelectedTown: () => {},
  selectedRegion: null,
  setSelectedRegion: () => {},
  townsForSelectedRegion: getAyrshireTowns().filter(t => t !== "All Towns"),
  detecting: false,
  detectLocation: () => {},
  locationError: null,
  townParam: undefined,
  towns: getAyrshireTowns(),
  townGroups: AYRSHIRE_TOWN_GROUPS,
});

export function TownProvider({ children }: { children: React.ReactNode }) {
  const [selection, setSelection] = useState<HubLocation>(() => {
    try {
      let prefs: { postcode?: string } | null = null;
      let addresses: Array<{ postcode?: string }> = [];
      try { prefs = JSON.parse(localStorage.getItem("user_ordering_prefs_v1") ?? "null"); } catch {}
      try { const saved = JSON.parse(localStorage.getItem("order_delivery_addresses") ?? "[]"); if (Array.isArray(saved)) addresses = saved; } catch {}
      return readLocation(localStorage.getItem(LOCATION_KEY), localStorage.getItem(STORAGE_KEY) ?? "All Towns", prefs?.postcode ?? addresses?.[0]?.postcode);
    } catch { return readLocation(null); }
  });
  const current = useRef(selection);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const onSelected = useRef<((town: string) => void) | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const selectedTown = selection.browseTown;
  const [detecting, setDetecting] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [townEntries, setTownEntries] = useState<TownEntry[]>(FALLBACK_TOWNS);
  const [townGroups, setTownGroups] = useState<TownGroup[]>(AYRSHIRE_TOWN_GROUPS);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/towns")
      .then(r => r.json())
      .then((data: TownEntry[]) => {
        if (Array.isArray(data) && data.length > 0) {
          _runtimeTowns = data;
          AYRSHIRE_TOWNS = getAyrshireTowns();
          setTownEntries(data);
          setTownGroups(buildTownGroups(data));
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const commit = useCallback((next: HubLocation) => {
    current.current = next;
    setSelection(next);
    setSelectedRegion(null);
    try {
      localStorage.setItem(LOCATION_KEY, JSON.stringify(next));
      localStorage.setItem(STORAGE_KEY, next.browseTown);
      let prefs: { fulfilment?: string } | null = null;
      try { prefs = JSON.parse(localStorage.getItem("user_ordering_prefs_v1") ?? "null"); } catch {}
      localStorage.setItem("user_ordering_prefs_v1", JSON.stringify({ fulfilment: prefs?.fulfilment === "collection" ? "collection" : "delivery", postcode: next.postcode }));
    } catch { /* browsing continues when storage is disabled */ }
  }, []);
  const invalidate = useCallback(() => { generation.current++; controller.current?.abort(); controller.current = null; setDetecting(false); }, []);
  const setSelectedTown = useCallback((town: string) => {
    if (town === current.current.browseTown) return;
    invalidate(); commit(browseArea(current.current, town));
  }, [commit, invalidate]);
  const closePicker = useCallback(() => { invalidate(); setPickerOpen(false); onSelected.current = undefined; }, [invalidate]);
  const openLocationPicker = useCallback((callback?: (town: string) => void) => { invalidate(); onSelected.current = callback; setLocationError(null); setPickerOpen(true); }, [invalidate]);
  const chooseArea = useCallback((town: string) => { invalidate(); commit(browseArea(current.current, town)); onSelected.current?.(town); closePicker(); }, [invalidate, commit, closePicker]);
  const savePostcode = useCallback(async (value: string, migration = false) => {
    invalidate(); const request = generation.current; const abort = new AbortController(); controller.current = abort;
    setDetecting(true); setLocationError(null);
    try {
      const next = await lookupLocationPostcode(value, townEntries, abort.signal);
      if (request !== generation.current) return false;
      commit(migration && current.current.mode === "area" ? { ...next, mode: "area", browseTown: current.current.browseTown } : next);
      if (!migration) { onSelected.current?.(next.browseTown); setPickerOpen(false); onSelected.current = undefined; }
      return true;
    } catch (error) { if (request === generation.current) setLocationError(error instanceof Error ? error.message : "Could not check your postcode. Please try again."); return false;
    } finally { if (request === generation.current) setDetecting(false); }
  }, [invalidate, commit, townEntries]);
  useEffect(() => {
    if (current.current.postcode && !current.current.postcodeTown) void savePostcode(current.current.postcode, true);
    return () => { generation.current++; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    const sync = (event: StorageEvent) => { if (event.key !== LOCATION_KEY) return; invalidate(); const next = readLocation(event.newValue); current.current = next; setSelection(next); setSelectedRegion(null); };
    window.addEventListener("storage", sync); return () => window.removeEventListener("storage", sync);
  }, [invalidate]);
  const detectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser.");
      return;
    }
    setDetecting(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const town = nearestTown(pos.coords.latitude, pos.coords.longitude, townEntries);
        setSelectedTown(town);
        setDetecting(false);
      },
      () => {
        setLocationError("Could not detect your location. Please select a town.");
        setDetecting(false);
      },
      { timeout: 8000 }
    );
  }, [setSelectedTown, townEntries]);

  const nearbyNames = selectedTown === "All Towns" ? [] : getNearbyTownNames(selectedTown, townEntries);
  // townParam is comma-separated nearby towns sent to API; undefined = no filter (All Towns)
  const townParam = nearbyTownParam(selection, townEntries);
  const towns = ["All Towns", ...townEntries.map(t => t.name)];
  const townsForSelectedRegion = selectedRegion
    ? (townGroups.find(g => g.borough === selectedRegion)?.towns ?? []).map(t => t.name)
    : townEntries.map(t => t.name);

  return (
    <TownContext.Provider value={{ selectedTown, deliveryPostcode: selection.postcode, locationLabel: selection.mode === "all" && !selection.postcode ? "Set your postcode · All Ayrshire" : locationLabel(selection), location: selection, openLocationPicker, setSelectedTown, selectedRegion, setSelectedRegion, townsForSelectedRegion, detecting, detectLocation, locationError, townParam, towns, townGroups }}>
      {children}
      <LocationPicker open={pickerOpen} onClose={closePicker} location={selection} towns={townEntries} busy={detecting} error={locationError} onPostcode={savePostcode} onArea={chooseArea} onUsePostcode={() => { invalidate(); const next = usePostcodeArea(current.current); commit(next); onSelected.current?.(next.browseTown); closePicker(); }} />
    </TownContext.Provider>
  );
}

export function useTown() {
  return useContext(TownContext);
}

export function useTownDropdown() {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return { ref, open, setOpen };
}
